import type { MouseEvent, ReactNode } from 'react';
import { Button, Spinner, cn } from '@orgus/ui';
import type { ButtonProps } from '@orgus/ui';

export interface BusyButtonProps extends Omit<ButtonProps, 'loading'> {
  /** Working: the spinner and `busyLabel` replace the icon and the label, at full colour. */
  busy?: boolean;
  /** What the button is doing, for example "Opening GitHub". */
  busyLabel: ReactNode;
}

/**
 * A button that says what it is doing while it works. Button's own `loading` hides the label and
 * dims the whole control like a disabled one, which on a primary action reads as an empty,
 * broken bar. Here the button keeps its colour, shows a spinner and a label, and refuses clicks
 * (aria-disabled) without the disabled look.
 */
export function BusyButton({ busy = false, busyLabel, icon, children, onClick, className, ...rest }: BusyButtonProps) {
  return (
    <Button
      {...rest}
      icon={busy ? <Spinner /> : icon}
      aria-busy={busy || undefined}
      aria-disabled={busy || undefined}
      data-busy={busy || undefined}
      className={cn(busy && 'cursor-progress', className)}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        if (busy) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
    >
      {busy ? busyLabel : children}
    </Button>
  );
}
